import { useCallback } from "react";
import { UserFieldsFragment } from "../__generated__/operations/graphql";
import { useIntercom } from "./intercom";
import {
    getAnalytics,
    setUserId,
    logEvent,
    resetAnalyticsData,
} from "../contexts/FirebaseAnalytics";
import { identify } from "../configs/mixpanel";
export type TrackingEventName =
    | "click_create_mission"
    | "click_create_invitation"
    | "click_create_colleague_invitation"
    | "click_send_invitation"
    | "click_resend_invitation"
    | "click_send_mission"
    | "click_close_create_mission"
    | "click_sign_contract"
    | "contract_signing_success"
    | "contract_signing_failed"
    | "contract_signing_declined"
    | "contract_signing_withdraw"
    | "contract_signing_error"
    | "click_confirm_terminate_mission"
    | "click_submit_timesheet"
    | "click_submit_payroll_breakdown"
    | "click_show_help"
    | "click_reject_timesheet"
    | "signup_showed"
    | "ask_activation_code_showed"
    | "signup_started"
    | "kyc_showed"
    | "kyc_started"
    | "kyc_completed"
    | "pre_signup_completed"
    | "signup_completed_client"
    | "signup_completed_private_client"
    | "signup_completed_worker"
    | "post_signup_completed_worker"
    | "mission_creation_started"
    | "mission_created"
    | "show_empty_mission_list";

export function useTracking() {
    const {
        login: loginIntercom,
        logout: logoutIntercom,
        logEvent: logEventIntercom,
    } = useIntercom();

    const login = useCallback(
        async (user: UserFieldsFragment) => {
            await setUserId(getAnalytics(), user.id);
            await logEvent(getAnalytics(), "login", { method: "phoneNumber" });
            await loginIntercom(user);
            identify(user.id);
        },
        [loginIntercom],
    );

    const logout = useCallback(async () => {
        await resetAnalyticsData?.(getAnalytics());
        await logoutIntercom();
    }, [logoutIntercom]);

    const trackAppOpen = useCallback(async () => {
        await logEvent(getAnalytics(), "app_open");
    }, []);

    const trackEvent = useCallback(
        async (
            eventName: TrackingEventName,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            params?: { [key: string]: any },
            useIntercom: boolean = false,
        ) => {
            if (useIntercom) {
                await logEventIntercom(eventName, params);
            }
            await logEvent(getAnalytics(), eventName, params);
            return true;
        },
        [logEventIntercom],
    );

    const trackScreenView = useCallback(async (screenName: string) => {
        await logEvent(getAnalytics(), "screen_view", {
            firebase_screen: screenName,
            firebase_screen_class: screenName,
        });
        return true;
    }, []);

    return {
        trackAppOpen,
        login,
        logout,
        trackEvent,
        trackScreenView,
    };
}
